import { Injectable, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import { AlarisProfileService, CustomValidators, DateFormatPipe } from '@campaign-portal/components-library';

import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import {
	DecisionMode,
	MessagePurpose,
	MessageSplitMode,
	PreModerationEventStatus,
	TrafficType,
	WhatsappButtonType,
	WhatsappHeaderType,
	WhatsappTemplateCategory,
	WhatsappTemplateType,
} from '@campaign-portal/namespace/common/enums';
import {
	Campaign,
	CampaignChannelSettings,
	CampaignStatus,
	CampaignViberSettings,
} from '@campaign-portal/namespace/entities/campaigns/specs';
import { FileParsedColumn } from '@campaign-portal/namespace/entities/files/specs';

import { CPCustomValidators } from '@helpers/validators/custom-validators';
import { CP_PERMISSIONS } from '@helpers/types/permissions';
import {
	CampaignChannelSettingsControls,
	CampaignControls,
	CampaignInfoControls,
	CampaignMessageControls,
	CampaignNativeControls,
	CampaignOmniSettingsControls,
	CampaignSettingsControls,
	CampaignSetupControls,
	CampaignStatisticsControls,
	CampaignViberSettingsControls,
} from '../shared/types/form-types';
import { ContactGroupsService } from '../../contacts/services/contact-groups.service';
import { MessageTemplatesService } from '../services/message-templates.service';
import { ShortLinkEntry, UrlShortenerUtilsService } from '../../url-shortener/url-shortener-utils.service';
import { CampaignRecipientsType } from './campaign-recipients/campaign-recipients.component';
import { TemplateWhatsappSettingsControls } from '../message-templates/templates/update-template/interfaces/message-template-form.interfaces';
import { WhatsappTemplateSettings } from '@campaign-portal/namespace/entities/templates/specs';
import { CampaignUtilsService } from '../services/campaign-utils.service';

export const MAX_PHONE_NUMBER_AMOUNT = 1000;
export const CONTACTS_SPLIT_PATTERN = /[\s;,]+/g;

export type ShortLinkChannelsMap = Map<TrafficType, ShortLinkEntry | undefined>;

@Injectable({
	providedIn: 'root',
})
export class CampaignWizardService {
	totalRecipients = signal(0);
	forbiddenFlashWarning = false;
	readonly shortLinkChannelsMap$ = new BehaviorSubject<ShortLinkChannelsMap>(new Map());

	constructor(
		private readonly contactGroupsService: ContactGroupsService,
		private readonly templatesService: MessageTemplatesService,
		private readonly campaignUtils: CampaignUtilsService,
		private readonly shortenerUtils: UrlShortenerUtilsService,
		private readonly profile: AlarisProfileService,
		private readonly translate: TranslateService,
		private readonly router: Router,
		private readonly dateFormatPipe: DateFormatPipe
	) {}

	_form!: FormGroup<CampaignControls>;

	get form(): FormGroup<CampaignControls> {
		return this._form;
	}

	set form(form: FormGroup<CampaignControls>) {
		this._form = form;
	}

	get infoControls(): CampaignInfoControls {
		return this._form.controls.info.controls;
	}

	get setupControls(): CampaignSetupControls {
		return this._form.controls.setup.controls;
	}

	get settingsControls(): CampaignSettingsControls {
		return this._form.controls.settings.controls;
	}

	get statisticsControls(): CampaignStatisticsControls {
		return this._form.controls.statistics.controls;
	}

	get contactsControls(): FormGroup<{
		file: FormControl<FileInfo | undefined>;
		plain: FormGroup<{
			text: FormControl<string>;
			code: FormControl<number | null>;
		}>;
		groups: FormControl<Id<exist>[] | undefined | null>;
	}> {
		return this._form.controls.setup.controls.native.controls.contacts;
	}

	get channelsFormArray(): FormArray<FormGroup<CampaignChannelSettingsControls>> {
		return this._form.controls.setup.controls.channels;
	}

	get channelsArray(): FormGroup<CampaignChannelSettingsControls>[] {
		return this._form.controls.setup.controls.channels.controls;
	}

	get contactsFile(): FormControl<FileInfo | undefined> {
		return this._form.controls.setup.controls.native.controls.contacts.controls.file;
	}

	get contactsPlain(): FormGroup<{ text: FormControl<string>; code: FormControl<number | null> }> {
		return this._form.controls.setup.controls.native.controls.contacts.controls.plain;
	}

	get contactGroups(): FormControl<Id<exist>[] | undefined | null> {
		return this._form.controls.setup.controls.native.controls.contacts.controls.groups;
	}

	get hasPreModeratedChannels(): boolean {
		const selectedChannelsSettings = this.channelsArray.map((fg) => fg.value);
		return selectedChannelsSettings.some((settings) => {
			const isPreModerated = this.campaignUtils.isPreModeratedChannel(settings.trafficType);
			const templateId = settings.message?.templateID;
			const isConfirmedTemplateUsed = Boolean(
				templateId && this.templatesService.map.get(templateId)?.status === PreModerationEventStatus.CONFIRMED
			);
			return isPreModerated && !isConfirmedTemplateUsed;
		});
	}

	findChannel(trafficType: TrafficType): FormGroup<CampaignChannelSettingsControls> | undefined {
		return this._form.controls.setup.controls.channels.controls.find((control) => {
			return control.controls.trafficType.value === trafficType;
		});
	}

	create(type: 'file' | 'native' = 'native', campaign?: Campaign): FormGroup<CampaignControls> {
		this._form = this.initializeForm(campaign);
		if (type === 'file') {
			this.disableNativeSetup();
		} else {
			this.disableFileSetup();
		}
		return this._form;
	}

	reset(): void {
		this._form = this.create();
		this.totalRecipients.set(0);
		this.forbiddenFlashWarning = false;
		this.shortLinkChannelsMap$.next(new Map());
	}

	prepare(type: 'file' | 'native' = 'native'): Campaign<null> {
		const baseCampaign: Campaign<null> = this.initializeBaseCampaign();

		let campaign: Campaign<null>;

		if (type === 'file') {
			campaign = this.prepareFileCampaign(baseCampaign);
		} else {
			campaign = this.prepareNativeCampaign(baseCampaign);
		}

		return campaign;
	}

	repeat(campaign: Campaign): void {
		if (campaign.id) {
			campaign.info.name = this.translate.instant('actions.repeat') + ' ' + campaign.info.name;
		}
		if (campaign.settings.isFlashed && !this.profile.user.details.isFlashed) {
			this.forbiddenFlashWarning = true;
			campaign.settings.isFlashed = false;
		}
		if (campaign.settings.isMo && !this.profile.user.details.isMo) {
			campaign.settings.isMo = false;
		}
		this.totalRecipients.set(campaign.statistics?.total ?? 0);
		this.create('native', campaign);
		this.enableContactsControls(this.determineRecipientType());
		this.checkShortLinks();
		this.router.navigate(['campaigns', 'campaign-wizard']);
	}

	checkShortLinks(): void {
		if (!this.profile.allowed([CP_PERMISSIONS.URL_SHORTENER_E])) {
			return;
		}
		const shortLinkChannelsMap: ShortLinkChannelsMap = new Map();
		this.channelsArray.forEach((ch) => {
			const channel = ch.getRawValue();
			switch (channel.trafficType) {
				case TrafficType.SMS:
				case TrafficType.VIBER:
				case TrafficType.TELEGRAM: {
					if (!channel.message) {
						return;
					}
					const message =
						channel.message.text ?? this.templatesService.map.get(channel.message.templateID)?.messageTemplate ?? '';
					shortLinkChannelsMap.set(channel.trafficType, this.shortenerUtils.extractShortLink(message));
					break;
				}
				case TrafficType.WHATSAPP: {
					const shortUrlButton = channel.omni?.whatsappSettings?.buttons.find((button) => button.shortUrl);
					const shortLinkEntry = this.shortenerUtils.extractShortLink(shortUrlButton?.shortUrl || '');
					shortLinkChannelsMap.set(channel.trafficType, shortLinkEntry);
				}
			}
		});
		this.shortLinkChannelsMap$.next(shortLinkChannelsMap);
	}

	determineRecipientType(): CampaignRecipientsType {
		const { plain, file } = this.contactsControls.value;
		return plain && plain.text
			? CampaignRecipientsType.MANUALLY
			: file
				? CampaignRecipientsType.FILE
				: this.profile.allowed([CP_PERMISSIONS.CONTACTS_R])
					? CampaignRecipientsType.GROUP
					: CampaignRecipientsType.MANUALLY;
	}

	enableContactsControls(recipientsType: CampaignRecipientsType): void {
		this.contactGroups.disable();
		this.contactsPlain.disable();
		this.contactsFile.disable();

		switch (recipientsType) {
			case CampaignRecipientsType.GROUP:
				this.contactGroups.enable();
				break;
			case CampaignRecipientsType.MANUALLY:
				this.contactsPlain.enable();
				break;
			case CampaignRecipientsType.FILE:
				this.contactsFile.enable();
				break;
		}
	}

	createWhatsappSettingsGroup(settings?: WhatsappTemplateSettings): FormGroup<TemplateWhatsappSettingsControls> {
		return new FormGroup<TemplateWhatsappSettingsControls>({
			category: new FormControl<WhatsappTemplateCategory>(settings?.category || WhatsappTemplateCategory.MARKETING, {
				nonNullable: true,
			}),
			language: new FormControl<string | null>(settings?.language || null),
			type: new FormControl<WhatsappTemplateType | null>(settings?.type || null),
			header: new FormGroup({
				type: new FormControl<WhatsappHeaderType>(settings?.header?.type || WhatsappHeaderType.TEXT, {
					nonNullable: true,
				}),
				value: new FormControl<string>(settings?.header?.value || '', { nonNullable: true }),
			}),
			footer: new FormControl<string | undefined>(settings?.footer, { nonNullable: true }),
			authentication: new FormGroup({
				code: new FormControl<string | undefined>(settings?.authentication?.code, { nonNullable: true }),
				codeExpirationDate: new FormControl<string | undefined>(settings?.authentication?.codeExpirationDate, {
					nonNullable: true,
				}),
			}),
			buttons: new FormArray(
				settings?.buttons?.map((button) => {
					return new FormGroup({
						type: new FormControl<WhatsappButtonType | null>(button?.type ?? null, Validators.required),
						name: new FormControl<string | undefined>(button?.name, {
							nonNullable: true,
						}),
						phone: new FormControl<string | undefined>(button?.phone, { nonNullable: true }),
						url: new FormControl<string | undefined>(button?.url, {
							nonNullable: true,
						}),
						shortUrl: new FormControl<string | undefined>(button?.shortUrl, {
							nonNullable: true,
						}),
						code: new FormControl<string | undefined>(button?.code, {
							nonNullable: true,
						}),
					});
				}) ?? []
			),
		});
	}

	private initializeForm(campaign?: Campaign): FormGroup<CampaignControls> {
		return new FormGroup<CampaignControls>({
			id: new FormControl<Id>(null),
			info: this.createInfoGroup(campaign),
			setup: new FormGroup({
				native: this.createNativeSetup(campaign),
				file: this.createFileSetup(),
				channels: this.createChannelsArray(campaign),
			}),
			settings: this.createSettingsGroup(campaign),
			statistics: this.createStatisticsGroup(),
		});
	}

	private prepareFileCampaign(baseCampaign: Campaign<null>): Campaign<null> {
		Object.assign(baseCampaign.setup, { file: {} });
		return Object.assign(baseCampaign, structuredClone(this.form.value));
	}

	private prepareNativeCampaign(baseCampaign: Campaign<null>): Campaign<null> {
		Object.assign(baseCampaign.setup, { native: { contacts: [] } });

		const campaign = Object.assign(baseCampaign, structuredClone(this.form.value));

		if (this.setupControls.native.controls.contacts.value.groups === null) {
			campaign.setup.native!.contacts.groups = this.contactGroupsService.list.map((cg) => cg.id);
		}
		if (this.setupControls.native.controls.contacts.value.plain) {
			campaign.setup.native!.contacts.plain = {
				code: this.contactsPlain.controls.code.value,
				text: this.contactsPlain.controls.text.value.replace(CONTACTS_SPLIT_PATTERN, ','),
			};
		}
		return campaign;
	}

	private initializeBaseCampaign(): Campaign<null> {
		return {
			id: null,
			info: {
				id: null,
				scheduled: '',
				status: CampaignStatus.SCHEDULED,
			},
			setup: {
				channels: [],
			},
			settings: {},
		};
	}

	private createInfoGroup(campaign?: Campaign): FormGroup<CampaignInfoControls> {
		const defaultName = `${this.translate.instant('campaigns.campaignName')} ${this.dateFormatPipe.transform(new Date())}`;

		return new FormGroup<CampaignInfoControls>({
			id: new FormControl<Id>(null),
			name: new FormControl<string>(campaign?.info.name ?? defaultName, { nonNullable: true }),
			description: new FormControl<string>(campaign?.info.description ?? '', { nonNullable: true }),
			photo: new FormControl<string>('', { nonNullable: true }),
			scheduled: new FormControl<string>(new Date().toISOString(), {
				nonNullable: true,
				validators: [Validators.required],
			}),
			status: new FormControl<CampaignStatus>(CampaignStatus.SCHEDULED, { nonNullable: true }),
		});
	}

	private createNativeSetup(campaign?: Campaign): FormGroup<CampaignNativeControls> {
		return new FormGroup<CampaignNativeControls>({
			contacts: new FormGroup(
				{
					file: new FormControl<FileInfo | undefined>(
						{
							value: campaign?.setup.native?.contacts.file,
							disabled: !campaign?.setup.native?.contacts.file,
						},
						{ nonNullable: true }
					),
					plain: new FormGroup({
						text: new FormControl<string>(
							{
								value: campaign?.setup.native?.contacts.plain?.text ?? '',
								disabled: !campaign?.setup.native?.contacts.plain?.text,
							},
							{
								nonNullable: true,
								validators: [
									CPCustomValidators.recipientsLimitAndPattern(MAX_PHONE_NUMBER_AMOUNT, CONTACTS_SPLIT_PATTERN),
								],
							}
						),
						code: new FormControl<number | null>({
							value: campaign?.setup.native?.contacts.plain?.code ?? null,
							disabled: !campaign?.setup.native?.contacts.plain?.code,
						}),
					}),
					groups: new FormControl<Id<exist>[] | undefined | null>(
						{
							value: campaign?.setup.native?.contacts.groups ?? [],
							disabled:
								!campaign?.setup.native?.contacts.groups || campaign?.setup.native?.contacts.groups.length === 0,
						},
						{ nonNullable: true, validators: [CustomValidators.requiredArrayOrNull] }
					),
				},
				[Validators.required]
			),
		});
	}

	private createFileSetup(): FormGroup<{
		file: FormControl<FileInfo | null>;
		columns: FormControl<FileParsedColumn[]>;
	}> {
		return new FormGroup({
			file: new FormControl<FileInfo | null>(null, { validators: Validators.required }),
			columns: new FormControl<FileParsedColumn[]>([], { nonNullable: true }),
		});
	}

	private createChannelsArray(campaign?: Campaign): FormArray<FormGroup<CampaignChannelSettingsControls>> {
		return new FormArray<FormGroup<CampaignChannelSettingsControls>>(
			campaign?.setup.channels?.map((chSetup) => this.createChannelGroup(chSetup)) ?? [],
			[Validators.required]
		);
	}

	private createChannelGroup(chSetup: CampaignChannelSettings): FormGroup<CampaignChannelSettingsControls> {
		const formGroup = new FormGroup<CampaignChannelSettingsControls>({
			sender: new FormControl<Id<exist> | null>(chSetup.sender, Validators.required),
			trafficType: new FormControl<TrafficType>(chSetup.trafficType, { nonNullable: true }),
			message: chSetup.message
				? new FormGroup<CampaignMessageControls>({
						text: new FormControl<string | undefined>(
							{
								value: chSetup.message.text,
								disabled: chSetup.omni?.whatsappSettings?.category === WhatsappTemplateCategory.AUTHENTICATION,
							},
							{
								nonNullable: true,
								validators: Validators.required,
							}
						),
						templateID: new FormControl<Id<exist> | null>(chSetup.message.templateID ?? null),
					})
				: undefined,
		});
		if (chSetup.omni) {
			formGroup.addControl('omni', this.createOmniSettingsGroup(chSetup));
		}
		return formGroup;
	}

	private createOmniSettingsGroup(chSetup: CampaignChannelSettings): FormGroup<CampaignOmniSettingsControls> {
		const form = new FormGroup<CampaignOmniSettingsControls>({
			fallbackType: new FormControl<DecisionMode>(chSetup.omni?.fallbackType ?? DecisionMode.BY_SUBMITTED, {
				nonNullable: true,
			}),
			ttl: new FormControl<number>(chSetup.omni?.ttl ?? 30, { nonNullable: true }),
		});
		if (chSetup.omni?.viberSettings) {
			form.addControl('viberSettings', this.createViberSettingsGroup(chSetup.omni.viberSettings));
		}
		if (chSetup.omni?.whatsappSettings) {
			form.addControl('whatsappSettings', this.createWhatsappSettingsGroup(chSetup.omni.whatsappSettings));
		}
		return form;
	}

	private createViberSettingsGroup(settings: CampaignViberSettings): FormGroup<CampaignViberSettingsControls> {
		const { messagePurpose, buttonActionUrl, imageUrl, buttonCaption } = settings;
		const purpose =
			messagePurpose ||
			(buttonActionUrl || imageUrl || buttonCaption ? MessagePurpose.PROMOTION : MessagePurpose.TRANSACTION);

		return new FormGroup<CampaignViberSettingsControls>({
			buttonActionUrl: new FormControl<string>(settings.buttonActionUrl ?? '', {
				nonNullable: true,
			}),
			imageUrl: new FormControl<string>(settings.imageUrl ?? '', { nonNullable: true }),
			buttonCaption: new FormControl<string>(settings.buttonCaption ?? '', {
				nonNullable: true,
			}),
			messagePurpose: new FormControl<MessagePurpose>(purpose, {
				nonNullable: true,
			}),
		});
	}

	private createSettingsGroup(campaign?: Campaign): FormGroup<CampaignSettingsControls> {
		return new FormGroup<CampaignSettingsControls>({
			failureAlertThreshold: new FormControl<number>(
				{
					value: campaign?.settings.failureAlertThreshold ?? 100,
					disabled: !campaign?.settings.failureAlertThreshold,
				},
				{ nonNullable: true }
			),
			emailReportAddresses: new FormControl<string>(
				{
					value: campaign?.settings.emailReportAddresses ?? '',
					disabled: !campaign?.settings.emailReportAddresses,
				},
				{ nonNullable: true, validators: [Validators.required] }
			),
			messageSplitMode: new FormControl<MessageSplitMode>(
				campaign?.settings.messageSplitMode ?? MessageSplitMode.SPLIT,
				{ nonNullable: true }
			),
			isFlashed: new FormControl<boolean>(campaign?.settings.isFlashed || false, { nonNullable: true }),
			isMo: new FormControl<boolean>(campaign?.settings.isMo || false, { nonNullable: true }),
		});
	}

	private createStatisticsGroup(): FormGroup<CampaignStatisticsControls> {
		return new FormGroup({
			preCalculatedCost: new FormGroup({
				min: new FormControl<number>(0, { nonNullable: true }),
				max: new FormControl<number>(0, { nonNullable: true }),
			}),
		});
	}

	private disableNativeSetup(): void {
		this._form.controls.setup.controls.native.disable();
		this._form.controls.setup.controls.file.enable();
	}

	private disableFileSetup(): void {
		this._form.controls.setup.controls.file.disable();
		this._form.controls.setup.controls.native.enable();
	}
}
